/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, Table, InputNumber, Spin, message, Select } from 'antd';
import { usePrivateStore } from '@/store/usePrivateStore';
import CommonJs from '@/utils/commonJs';
import { BaoCaoDuAnCongViecView } from '@/types/action-plan/ActionPlan/BaoCaoDuAnCongViecView';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { tabletansuat } from '@/types/performance/bangtuansuat';
import { DuAnCongViecTienDoDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecTienDoDTO';
import { DuAnCongViecChiTieuItem } from '@/types/action-plan/DuAnView';
import {
  buildInitialChiTieuByNgayMap,
  buildTienDosPayload,
  chiTieuPeriodDayKey,
  distributeChiTieuByHinhThuc,
  formatMoneyInputDisplay,
  generateReportsFallback,
  getChiTieuConstraintError,
  indexExistingTienDosById,
  mergeTanSuatFromApi,
  parseMoneyInput,
  reportsToTanSuatList,
  resolveNgayBaoCaoTienDo,
  resolveTanSuatNumber,
  round2,
} from './chiaChiTieuModal.helpers';
import { useAuthStore } from '@/store/useAuthStore';
import { baocaoApi } from '@/services/action-plan/baocao';

interface ChiaChiTieuModalProps {
  visible: boolean;
  onCancel: () => void;
  onSaveChiaChiTieuSuccess?: (data?: any[] | { tableData: any[]; dicTienDos: Record<string, any[]>; idCongViec: string }) => void;
  onSaveSuccess?: () => void;
  record: any;
  listChiTieu: any[];
  readOnly?: boolean;
  /** true: chỉ onApply + Lưu bảng; false: gọi savetablechiachitiet */
  persistViaParentSave?: boolean;
  /** panel = không bọc Modal (dùng trong Popover) */
  variant?: 'modal' | 'panel';
}

const HINH_THUC_OPTIONS = [
  { value: 0, label: 'Tổng ' },
  { value: 1, label: 'Trung bình ' },
  { value: 2, label: 'Tiến độ ' },
];

/**
 * Format ngày dạng dd/mm (ưu tiên parse YYYY-MM-DD từ chuỗi ISO để tránh lệch timezone).
 * Ví dụ: "2026-03-31T00:00:00" -> "31/03"
 */
function formatNgayKetThucDDMM(value?: string | Date | null): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const datePart = value.includes('T') ? value.slice(0, 10) : value.slice(0, 10);
    const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}/${m[2]}`;

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}`;
    }
    return '';
  }

  const d = value;
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

const ChiaChiTieuModal: React.FC<ChiaChiTieuModalProps> = ({
  visible,
  onCancel,
  record,
  onSaveChiaChiTieuSuccess,
  readOnly = false,
  persistViaParentSave = false,
  variant = 'modal',
}) => {
  const [loading, setLoading] = useState(false);
  const [baoCaoData, setBaoCaoData] = useState<BaoCaoDuAnCongViecView | null>(null);
  const [tanSuatList, setTanSuatList] = useState<tabletansuat[]>([]);
  const [chiTieu, setChiTieu] = useState<number>(0);
  const [hinhThuc, setHinhThuc] = useState<number>(0);
  const initialChiTieuByNgayRef = useRef<Record<string, number>>({});
  const initialHinhThucRef = useRef<number>(0);
  const initialChiTieuRef = useRef<number>(0);
  const { tenantID } = useAuthStore();
  const { getPhanHe } = usePrivateStore();

  const sumChiTieu = useMemo(
    () => (tanSuatList || []).reduce((acc, ts) => acc + Number(ts.chiTieu ?? 0), 0),
    [tanSuatList]
  );

  const remainingHint = useMemo(() => {
    const target = Number(chiTieu || 0);
    if (hinhThuc === 0) return round2(target - sumChiTieu);
    if (hinhThuc === 1) {
      const thieu = (tanSuatList || []).reduce((acc, ts) => {
        const v = Number(ts.chiTieu ?? 0);
        return acc + Math.max(0, target - v);
      }, 0);
      return round2(thieu);
    }
    return 0;
  }, [chiTieu, hinhThuc, sumChiTieu, tanSuatList]);

  const shouldHighlightMissing = !readOnly && remainingHint > 0.01;

  const loadTimeLine = useCallback(async () => {
    if (!record) return;

    const cv = record.congViec || record.originalData?.congViec;
    const id = cv?.id || record.congViec.id || '';
    const idPhuTrach = cv?.nguoiPhuTrachID || '';
    const totalChiTieu = Number(cv?.chiTieu) || 0;
    const ht = cv?.hinhThuc;
    const hinhThucInit = ht === 0 || ht === 1 || ht === 2 ? ht : 0;

    setChiTieu(totalChiTieu);
    setHinhThuc(hinhThucInit);
    initialHinhThucRef.current = hinhThucInit;
    initialChiTieuRef.current = totalChiTieu;

    const startDate =
      record.congViec?.ngayBatDau || record.originalData?.congViec?.ngayBatDau || cv?.ngayBatDau;
    const endDate = record.congViec?.ngayKetThuc || record.originalData?.congViec?.ngayKetThuc || cv?.ngayKetThuc;

    if (!id) {
      message.error('Không tìm thấy ID công việc');
      return;
    }

    try {
      setLoading(true);

      const phanHe = getPhanHe() || 'bsc';
      const tanSuat = resolveTanSuatNumber(record);
      const congViecDto = (record?.congViec || record?.originalData?.congViec || {}) as DuAnCongViecDTO;
      congViecDto.kanbans = [];

      const response: any = await baocaoApi.gettablechiachitieu({
        PhanHe: phanHe,
        IDPhuTrach: idPhuTrach,
        TanSuat: String(tanSuat),
        CongViec: congViecDto,
      });

      if (!response?.data) {
        message.warning('Không tải được báo cáo từ server');
        setBaoCaoData(null);
        setTanSuatList([]);
        return;
      }

      const baoCao = response.data as BaoCaoDuAnCongViecView;
      setBaoCaoData(baoCao);

      let list = mergeTanSuatFromApi([...(baoCao.listTanSuat || [])]);

      if (list.length === 0 && startDate && endDate) {
        const reports = generateReportsFallback(
          startDate as string,
          endDate as string,
          resolveTanSuatNumber(record),
          totalChiTieu
        );
        list = reportsToTanSuatList(reports);
      }

      if (list.length === 0) {
        message.warning('Không có kỳ báo cáo. Kiểm tra ngày bắt đầu/kết thúc và tần suất.');
      }

      initialChiTieuByNgayRef.current = buildInitialChiTieuByNgayMap(list);
      setTanSuatList(list);
    } catch (error) {
      console.error('Error loading báo cáo (ChiaChiTieu):', error);
      message.error('Không thể tải dữ liệu báo cáo');
      setBaoCaoData(null);
      setTanSuatList([]);
    } finally {
      setLoading(false);
    }
  }, [getPhanHe, record]);

  useEffect(() => {
    if (visible && record) void loadTimeLine();
  }, [visible, record, loadTimeLine]);

  const handleAutoDistribute = useCallback(
    (nextHinhThuc?: number) => {
      if (tanSuatList.length === 0) return;
      const effectiveHinhThuc = nextHinhThuc ?? hinhThuc;
      const amounts = distributeChiTieuByHinhThuc(chiTieu, tanSuatList.length, effectiveHinhThuc);
      setTanSuatList((prev) =>
        prev.map((ts, i) => ({
          ...ts,
          chiTieu: amounts[i] ?? 0,
          chiTieuTienDo: amounts[i] ?? 0,
        }))
      );
    },
    [chiTieu, hinhThuc, tanSuatList.length]
  );

  const handleHinhThucChange = (value: number) => {
    setHinhThuc(value);
    handleAutoDistribute(value);
  };

  useEffect(() => {
    if (!visible) return;
    if (tanSuatList.length === 0) return;
    if (hinhThuc === 0 || hinhThuc === 1) handleAutoDistribute(hinhThuc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiTieu]);

  const validateConstraints = useCallback((): boolean => {
    const values = tanSuatList.map((x) => Number(x.chiTieu ?? 0));
    const err = getChiTieuConstraintError(hinhThuc, chiTieu, values);
    if (err) {
      message.error(err);
      return false;
    }
    return true;
  }, [chiTieu, hinhThuc, tanSuatList]);

  const handleApply = async () => {
    const cv = record?.congViec || record?.originalData?.congViec;
    const idCongViec = cv?.id || record?.congViec?.id || '';

    if (!idCongViec) {
      message.error('Không tìm thấy ID công việc');
      return;
    }
    if (tanSuatList.length === 0) {
      message.warning('Không có kỳ báo cáo để áp dụng');
      return;
    }
    if (!validateConstraints()) return;
    if (!persistViaParentSave && !baoCaoData) {
      message.error('Chưa có dữ liệu báo cáo để lưu. Vui lòng tải lại.');
      return;
    }

    const site = tenantID
    const existingRows = (baoCaoData?.dicTienDos?.[idCongViec] ?? []) as DuAnCongViecTienDoDTO[];
    const existingById = indexExistingTienDosById(existingRows);

    const tienDos = buildTienDosPayload(tanSuatList, {
      idCongViec,
      site: site || '',
      existingById,
      cvNgayBatDau: cv?.ngayBatDau,
      cvNgayKetThuc: cv?.ngayKetThuc,
    });

    const dicTienDos: Record<string, DuAnCongViecTienDoDTO[]> = {
      [idCongViec]: tienDos,
    };

    if (persistViaParentSave) {
      const initialMap = initialChiTieuByNgayRef.current || {};
      const changedListChiTieu: DuAnCongViecChiTieuItem[] = (tanSuatList || [])
        .map((ts) => {
          const key = chiTieuPeriodDayKey(ts);
          const prevVal = initialMap[key];
          const next = Number(ts.chiTieu ?? 0);
          const changed =
            prevVal === undefined ? true : round2(prevVal) !== round2(next);
          if (!changed) return null;
          const prevRow = existingById.get(String(ts.idbaocao || ts.id || ''));
          const rowId =
            (prevRow?.id && String(prevRow.id).trim()) ||
            String(ts.idbaocao || ts.id || CommonJs.createTempID());
          return {
            ngay: resolveNgayBaoCaoTienDo(ts, prevRow, cv?.ngayBatDau, cv?.ngayKetThuc) as any,
            chiTieu: next,
            id: rowId,
            ID: rowId,
          };
        })
        .filter(Boolean) as DuAnCongViecChiTieuItem[];

      const hinhThucChanged = hinhThuc !== initialHinhThucRef.current;
      const chiTieuChanged = round2(chiTieu) !== round2(initialChiTieuRef.current);

      if (changedListChiTieu.length === 0 && !hinhThucChanged && !chiTieuChanged) {
        onCancel();
        return;
      }

      onSaveChiaChiTieuSuccess?.({
        dicTienDos,
        ...(changedListChiTieu.length > 0 ? { listChiTieu: changedListChiTieu } : {}),
        idCongViec,
        ...(hinhThucChanged ? { hinhThuc } : {}),
        ...(chiTieuChanged ? { chiTieu } : {}),
      } as any);

      onCancel();
      return;
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Kỳ báo cáo',
        dataIndex: 'tungay',
        key: 'tungay',
        width: 100,
        render: (_: string, row: tabletansuat) => {
          const label =
            formatNgayKetThucDDMM(row.ketthuc);
          const title = row.title || row.baocaochoky || '';
          return (
            <div>
              <div style={{ fontWeight: 600 }}>{title}</div>
              <div style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
            </div>
          );
        },
      },
      {
        title: 'Chỉ tiêu',
        dataIndex: 'chiTieu',
        key: 'chiTieu',
        width: 100,
        render: (_: number, row: tabletansuat, index: number) => (
          <InputNumber
            controls={false}
            changeOnBlur={false}
            value={row.chiTieu}
            disabled={readOnly}
            status={shouldHighlightMissing ? 'error' : undefined}
            formatter={(value, info) => formatMoneyInputDisplay(value, info.userTyping, info.input)}
            parser={parseMoneyInput}
            onChange={(val) => {
              const v = val == null ? 0 : Number(val);
              setTanSuatList((prev) => {
                const next = [...prev];
                const safeV = Number.isFinite(v) ? v : 0;
                const target = Number(chiTieu || 0);
                if (hinhThuc === 0) {
                  const othersSum = next.reduce(
                    (acc, ts, j) => (j === index ? acc : acc + Number(ts.chiTieu ?? 0)),
                    0
                  );
                  const maxForCell = Math.max(0, round2(target - othersSum));
                  const capped = Math.min(safeV, maxForCell);
                  next[index] = { ...next[index], chiTieu: capped, chiTieuTienDo: capped };
                } else {
                  next[index] = { ...next[index], chiTieu: safeV, chiTieuTienDo: safeV };
                }
                return next;
              });
            }}
            min={0}
            style={{
              width: '100%',
              fontWeight: 600,
              borderColor: hinhThuc === 1
                ? round2(Number(row.chiTieu ?? 0)) !== round2(Number(chiTieu || 0))
                  ? '#ff4d4f'
                  : undefined
                : hinhThuc === 2 &&
                  index > 0 &&
                  Number(row.chiTieu ?? 0) < Number(tanSuatList[index - 1]?.chiTieu ?? 0)
                  ? '#ff4d4f'
                  : undefined,
            }}
          />
        ),
      },
    ],
    [chiTieu, hinhThuc, readOnly, tanSuatList, shouldHighlightMissing]
  );

  const body = (
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16, marginTop: variant === 'panel' ? 0 : 16 }}>
          <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong>Tổng chỉ tiêu:</strong>{' '}
              {readOnly ? (
                chiTieu
              ) : (
                <InputNumber
                  variant="borderless"
                  value={CommonJs.formatMoney(chiTieu)}
                  style={{ width: 'max-content', marginLeft: 12, fontWeight: 500 }}
                  controls={false}
                />
              )}
            </div>

          </div>

          <div style={{ marginBottom: 8 }}>
            <strong>Hình thức tính:</strong>
            <Select
              variant="borderless"
              style={{ marginLeft: 8, width: 120, fontWeight: 600 }}
              value={hinhThuc}
              disabled={readOnly}
              onChange={handleHinhThucChange}
              options={HINH_THUC_OPTIONS}
            />
          </div>

          {remainingHint > 0 && (
            <div style={{ fontWeight: 600, color: '#ff4d4f' }}>
              Còn thiếu: {CommonJs.formatMoney(remainingHint)}
            </div>
          )}
        </div>


        <Table
          columns={columns}
          dataSource={tanSuatList}
          rowKey={(r, i) => r.idbaocao || r.id || `row-${i}`}
          pagination={false}
          scroll={{ y: 300 }}
          locale={{
            emptyText: 'Không có dữ liệu',
          }}
        />
      </Spin>
  );

  const footerBtns = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: variant === 'panel' ? 12 : 0 }}>
      {readOnly ? null : (
        <>
          <Button key="auto" onClick={() => handleAutoDistribute()} disabled={loading || tanSuatList.length === 0}>
            Chia tự động
          </Button>
          <Button key="apply" type="primary" onClick={() => void handleApply()}>
            Áp dụng
          </Button>
        </>
      )}
      {variant === 'panel' ? (
        <Button key="close" onClick={onCancel}>
          Đóng
        </Button>
      ) : null}
    </div>
  );

  if (variant === 'panel') {
    if (!visible) return null;
    return (
      <div style={{ width: 360, maxWidth: 'min(360px, calc(100vw - 32px))' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: '#344054' }}>Chia chỉ tiêu</div>
        {body}
        {footerBtns}
      </div>
    );
  }

  return (
    <Modal
      title="Chia chỉ tiêu"
      open={visible}
      onCancel={onCancel}
      width={400}
      maskClosable={false}
      footer={
        readOnly
          ? null
          : [
              <Button key="auto" onClick={() => handleAutoDistribute()} disabled={loading || tanSuatList.length === 0}>
                Chia tự động
              </Button>,
              <Button key="apply" type="primary" onClick={() => void handleApply()}>
                Áp dụng
              </Button>,
            ]
      }
    >
      {body}
    </Modal>
  );
};

export default ChiaChiTieuModal;
